import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert, Table,
  Nav, NavItem, NavLink, TabContent, TabPane
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/TableContainer";

const BASE_URL = "https://harmanagelapi.stechomeyazilim.info";

// DURUM tinyint helpers (ILAN_TEKLIFLERI)
const teklifDurumText = d =>
  d === 1 ? "Kabul Edildi" : d === 2 ? "Reddedildi" : "Bekliyor";
const teklifDurumColor = d =>
  d === 1 ? "success" : d === 2 ? "danger" : "warning";

const IlanYonetimi = () => {
  document.title = "İlan Yönetimi | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  // ─── DATA ────────────────────────────────────────────────────
  const [ilanlar, setIlanlar] = useState([]);
  const [kategoriler, setKategoriler] = useState([]);
  const [teklifSayilari, setTeklifSayilari] = useState({}); // { ilanId: count }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  // ─── FILTERS ─────────────────────────────────────────────────
  const [filterDurum, setFilterDurum] = useState("all");
  const [filterKategori, setFilterKategori] = useState("all");
  const [filterTip, setFilterTip] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // ─── BULK SELECT ─────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // ─── DETAIL MODAL ─────────────────────────────────────────────
  const [detailModal, setDetailModal] = useState(false);
  const [detailIlan, setDetailIlan] = useState(null);
  const [detailTab, setDetailTab] = useState("1");
  const [detailLoading, setDetailLoading] = useState(false);
  const [ilanMedya, setIlanMedya] = useState([]);
  const [ilanTeklifler, setIlanTeklifler] = useState([]);
  const [ilanDegerlemeler, setIlanDegerlemeler] = useState([]);
  const [ilanSikayetler, setIlanSikayetler] = useState([]);

  // ─── EDIT MODAL ───────────────────────────────────────────────
  const [editModal, setEditModal] = useState(false);
  const [editIlan, setEditIlan] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // ─── FETCH ───────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [ilanRes, katRes, teklifRes] = await Promise.all([
        fetch(
          `${BASE_URL}/ILANLAR?$top=1000&$orderby=OLUSTURMA_TARIHI desc` +
          `&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress,CEP_NO),KATEGORILER($select=AD,ID)`,
          { headers }
        ),
        fetch(`${BASE_URL}/KATEGORILER?$orderby=AD asc&$select=ID,AD`, { headers }),
        fetch(`${BASE_URL}/ILAN_TEKLIFLERI?$select=ID,ILAN_ID,DURUM&$top=5000`, { headers }),
      ]);

      const [ilanData, katData, teklifData] = await Promise.all([
        ilanRes.json(), katRes.json(), teklifRes.json(),
      ]);

      const ilanList = ilanData.value || [];
      const teklifList = teklifData.value || [];

      // Teklif sayısı per ilan
      const counts = {};
      teklifList.forEach(t => {
        counts[t.ILAN_ID] = (counts[t.ILAN_ID] || 0) + 1;
      });

      setIlanlar(ilanList);
      setKategoriler(katData.value || []);
      setTeklifSayilari(counts);
    } catch {
      setError("Veriler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── DETAIL FETCH ─────────────────────────────────────────────
  const openDetail = async (ilan) => {
    setDetailIlan(ilan);
    setDetailTab("1");
    setDetailModal(true);
    setDetailLoading(true);
    setIlanMedya([]);
    setIlanTeklifler([]);
    setIlanDegerlemeler([]);
    setIlanSikayetler([]);
    try {
      const [mRes, tRes, dRes, sRes] = await Promise.all([
        fetch(`${BASE_URL}/ILANLAR_MEDYA?$filter=ILAN_ID eq ${ilan.ID}&$orderby=SIRA asc`, { headers }),
        fetch(`${BASE_URL}/ILAN_TEKLIFLERI?$filter=ILAN_ID eq ${ilan.ID}&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress)&$orderby=TEKLIF_TARIHI desc`, { headers }),
        fetch(`${BASE_URL}/ILAN_DEGERLEMELERI?$filter=ILAN_ID eq ${ilan.ID}&$expand=KULLANICILAR($select=AD_SOYAD)&$orderby=TARIH desc`, { headers }),
        fetch(`${BASE_URL}/ILAN_SIKAYETLERI?$filter=ILAN_ID eq ${ilan.ID}&$expand=KULLANICILAR($select=AD_SOYAD)&$orderby=TARIH desc`, { headers }),
      ]);
      const [mData, tData, dData, sData] = await Promise.all([
        mRes.json(), tRes.json(), dRes.json(), sRes.json(),
      ]);
      setIlanMedya(mData.value || []);
      setIlanTeklifler(tData.value || []);
      setIlanDegerlemeler(dData.value || []);
      setIlanSikayetler(sData.value || []);
    } catch { }
    finally { setDetailLoading(false); }
  };

  // ─── STATUS CHANGE ────────────────────────────────────────────
  const handleStatusChange = async (id, newStatus) => {
    try {
      await fetch(`${BASE_URL}/ILANLAR(${id})`, {
        method: "PATCH", headers,
        body: JSON.stringify({ AKTIF_DURUM: newStatus }),
      });
      setSuccessMsg("Durum güncellendi.");
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch { setError("Durum güncellenemedi."); }
  };

  // ─── DELETE ───────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Bu ilanı silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/ILANLAR(${id})`, { method: "DELETE", headers });
      setSuccessMsg("İlan silindi.");
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch { setError("Silme işlemi başarısız."); }
  };

  // ─── BULK ACTIONS ─────────────────────────────────────────────
  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    const label = action === "sil" ? "silmek" : action === "aktif" ? "aktifleştirmek" : "pasifleştirmek";
    if (!window.confirm(`${selectedIds.size} ilanı ${label} istiyor musunuz?`)) return;
    setBulkLoading(true);
    try {
      await Promise.all([...selectedIds].map(id => {
        if (action === "sil") {
          return fetch(`${BASE_URL}/ILANLAR(${id})`, { method: "DELETE", headers });
        }
        return fetch(`${BASE_URL}/ILANLAR(${id})`, {
          method: "PATCH", headers,
          body: JSON.stringify({ AKTIF_DURUM: action }),
        });
      }));
      setSuccessMsg(`${selectedIds.size} ilan güncellendi.`);
      setSelectedIds(new Set());
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch { setError("Toplu işlem başarısız."); }
    finally { setBulkLoading(false); }
  };

  // ─── QUICK EDIT SAVE ──────────────────────────────────────────
  const handleEditSave = async () => {
    setEditLoading(true);
    try {
      await fetch(`${BASE_URL}/ILANLAR(${editIlan.ID})`, {
        method: "PATCH", headers,
        body: JSON.stringify({
          FIYAT: parseFloat(editIlan.FIYAT) || null,
          INDIRIMLI_FIYAT: editIlan.INDIRIMLI_FIYAT ? parseFloat(editIlan.INDIRIMLI_FIYAT) : null,
          STOK_ADEDI: parseInt(editIlan.STOK_ADEDI) || null,
          AKTIF_DURUM: editIlan.AKTIF_DURUM,
          IsPremium: editIlan.IsPremium,
          IsBold: editIlan.IsBold,
          TEKLIFE_ACIK_MI: editIlan.TEKLIFE_ACIK_MI,
          BITIS_TARIHI: editIlan.BITIS_TARIHI || null,
          ACIKLAMA: editIlan.ACIKLAMA || null,
        }),
      });
      setSuccessMsg("İlan güncellendi.");
      setEditModal(false);
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch { setError("Güncelleme başarısız."); }
    finally { setEditLoading(false); }
  };

  // ─── COMPUTED ─────────────────────────────────────────────────
  const stats = useMemo(() => {
    const aktif = ilanlar.filter(i => i.AKTIF_DURUM === "aktif").length;
    const pasif = ilanlar.filter(i => i.AKTIF_DURUM === "pasif").length;
    const premium = ilanlar.filter(i => i.IsPremium).length;
    const totalTeklif = Object.values(teklifSayilari).reduce((a, b) => a + b, 0);
    return { total: ilanlar.length, aktif, pasif, premium, totalTeklif };
  }, [ilanlar, teklifSayilari]);

  const filteredIlanlar = useMemo(() => {
    return ilanlar.filter(i => {
      if (filterDurum !== "all" && i.AKTIF_DURUM !== filterDurum) return false;
      if (filterKategori !== "all" && String(i.KATEGORI_ID) !== filterKategori) return false;
      if (filterTip === "satis" && !i.SATIS_MI) return false;
      if (filterTip === "teklif" && !i.TEKLIFE_ACIK_MI) return false;
      if (filterTip === "premium" && !i.IsPremium) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (
          i.AD?.toLowerCase().includes(q) ||
          i.KULLANICILAR?.AD_SOYAD?.toLowerCase().includes(q) ||
          String(i.ID).includes(q)
        );
      }
      return true;
    });
  }, [ilanlar, filterDurum, filterKategori, filterTip, searchQuery]);

  const statusBadge = (s) => {
    if (s === "aktif") return <Badge color="success">Aktif</Badge>;
    if (s === "pasif") return <Badge color="danger">Pasif</Badge>;
    if (s === "beklemede") return <Badge color="warning">Beklemede</Badge>;
    return <Badge color="secondary">{s || "?"}</Badge>;
  };

  // ─── TOGGLE SELECT ────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredIlanlar.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredIlanlar.map(i => i.ID)));
    }
  };

  // ─── TABLE COLUMNS ────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      id: "select",
      header: () => (
        <Input
          type="checkbox"
          checked={selectedIds.size > 0 && selectedIds.size === filteredIlanlar.length}
          onChange={toggleSelectAll}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => (
        <Input
          type="checkbox"
          checked={selectedIds.has(cell.row.original.ID)}
          onChange={() => toggleSelect(cell.row.original.ID)}
          onClick={e => e.stopPropagation()}
        />
      ),
    },
    {
      header: "Görsel",
      accessorKey: "GORSEL_YOLU",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const url = cell.getValue();
        return url ? (
          <img
            src={url}
            alt="ilan"
            style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6 }}
          />
        ) : (
          <div
            className="bg-light border rounded d-flex align-items-center justify-content-center text-muted"
            style={{ width: 44, height: 44, borderRadius: 6 }}
          >
            <i className="bx bx-image font-size-18" />
          </div>
        );
      },
    },
    {
      header: "İlan",
      accessorKey: "AD",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => {
        const i = cell.row.original;
        return (
          <div style={{ maxWidth: 240 }}>
            <div className="fw-semibold text-truncate">{i.AD}</div>
            <div className="d-flex flex-wrap gap-1 mt-1">
              {i.KATEGORILER?.AD && (
                <span className="badge bg-light text-dark border font-size-10">
                  {i.KATEGORILER.AD}
                </span>
              )}
              {i.KONUM_IL && (
                <span className="badge bg-light text-muted border font-size-10">
                  <i className="bx bx-map-pin me-1" />{i.KONUM_IL}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      header: "Satıcı",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const u = cell.getValue();
        return u ? (
          <div>
            <div className="fw-medium font-size-13">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : <span className="text-muted">-</span>;
      },
    },
    {
      header: "Fiyat",
      accessorKey: "FIYAT",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const fiyat = cell.getValue();
        const indirimli = cell.row.original.INDIRIMLI_FIYAT;
        const birim = cell.row.original.URUN_BIRIMI;
        return (
          <div>
            {indirimli ? (
              <>
                <div className="text-decoration-line-through text-muted font-size-11">
                  {Number(fiyat).toLocaleString("tr-TR")} ₺
                </div>
                <div className="fw-bold text-success">
                  {Number(indirimli).toLocaleString("tr-TR")} ₺
                </div>
              </>
            ) : (
              <div className="fw-medium">
                {fiyat ? `${Number(fiyat).toLocaleString("tr-TR")} ₺` : "-"}
              </div>
            )}
            {birim && <small className="text-muted">/ {birim}</small>}
          </div>
        );
      },
    },
    {
      header: "Stok / Görüntülenme",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const i = cell.row.original;
        return (
          <div className="font-size-12">
            <div><i className="bx bx-package me-1 text-muted" />{i.STOK_ADEDI ?? "∞"}</div>
            <div><i className="bx bx-show me-1 text-muted" />{i.GÖRÜNTÜLENME_SAYISI ?? 0}</div>
          </div>
        );
      },
    },
    {
      header: "Teklifler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const count = teklifSayilari[cell.row.original.ID] || 0;
        return (
          <span className={`badge font-size-12 ${count > 0 ? "bg-primary" : "bg-light text-dark"}`}>
            {count}
          </span>
        );
      },
    },
    {
      header: "Durum",
      accessorKey: "AKTIF_DURUM",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const i = cell.row.original;
        return (
          <div>
            {statusBadge(cell.getValue())}
            <div className="d-flex flex-wrap gap-1 mt-1">
              {i.IsPremium && <Badge color="warning" className="font-size-10">Premium</Badge>}
              {i.IsBold && <Badge color="dark" className="font-size-10">Kalın</Badge>}
              {i.SATIS_MI && <Badge color="info" className="font-size-10">Satış</Badge>}
              {i.TEKLIFE_ACIK_MI && <Badge color="success" className="font-size-10">Teklif</Badge>}
              {i.IsStory && <Badge color="purple" style={{ background: "#7367f0" }} className="font-size-10">Story</Badge>}
            </div>
          </div>
        );
      },
    },
    {
      header: "Tarih",
      accessorKey: "OLUSTURMA_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        const bitis = cell.row.original.BITIS_TARIHI;
        return (
          <div className="font-size-12">
            <div>{d ? new Date(d).toLocaleDateString("tr-TR") : "-"}</div>
            {bitis && (
              <div className="text-danger">
                <i className="bx bx-time me-1" />
                {new Date(bitis).toLocaleDateString("tr-TR")}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const i = cell.row.original;
        const isAktif = i.AKTIF_DURUM === "aktif";
        return (
          <div className="d-flex gap-1">
            <Button color="info" size="sm" outline title="Detay" onClick={() => openDetail(i)}>
              <i className="bx bx-show" />
            </Button>
            <Button
              color="primary" size="sm" outline title="Düzenle"
              onClick={() => { setEditIlan({ ...i }); setEditModal(true); }}
            >
              <i className="bx bx-pencil" />
            </Button>
            <Button
              color={isAktif ? "warning" : "success"} size="sm" outline
              title={isAktif ? "Pasifleştir" : "Aktifleştir"}
              onClick={() => handleStatusChange(i.ID, isAktif ? "pasif" : "aktif")}
            >
              <i className={`bx ${isAktif ? "bx-pause" : "bx-play"}`} />
            </Button>
            <Button color="danger" size="sm" outline title="Sil" onClick={() => handleDelete(i.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [teklifSayilari, selectedIds, filteredIlanlar]);

  // ─── RENDER ───────────────────────────────────────────────────
  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="İlan Yönetimi" />

          {successMsg && (
            <Alert color="success" className="border-0" toggle={() => setSuccessMsg("")}>
              <i className="mdi mdi-check-circle me-2" />{successMsg}
            </Alert>
          )}
          {error && (
            <Alert color="danger" className="border-0" toggle={() => setError(null)}>
              <i className="mdi mdi-alert-outline me-2" />{error}
            </Alert>
          )}

          {/* ── İSTATİSTİK KARTLARI ───────────────────────────── */}
          <Row className="mb-4">
            {[
              { label: "Toplam İlan", value: stats.total, icon: "bx-store-alt", color: "primary" },
              { label: "Aktif", value: stats.aktif, icon: "bx-check-circle", color: "success" },
              { label: "Pasif", value: stats.pasif, icon: "bx-pause-circle", color: "danger" },
              { label: "Premium", value: stats.premium, icon: "bxs-star", color: "warning" },
              { label: "Toplam Teklif", value: stats.totalTeklif, icon: "bx-message-square-dots", color: "info" },
            ].map((s, i) => (
              <Col key={i} xs={6} md={4} xl>
                <Card className="mini-stats-wid mb-3">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium mb-1 font-size-13">{s.label}</p>
                        <h4 className="mb-0">
                          {isLoading ? <Spinner size="sm" color={s.color} /> : s.value}
                        </h4>
                      </div>
                      <div className={`avatar-sm rounded-circle bg-${s.color} align-self-center mini-stat-icon`}>
                        <span className={`avatar-title rounded-circle bg-${s.color}`}>
                          <i className={`bx ${s.icon} font-size-20`} />
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ── ANA TABLO ─────────────────────────────────────── */}
          <Card className="shadow-sm">
            <CardBody>
              {/* Toolbar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <h4 className="card-title mb-1">Tüm İlanlar</h4>
                  <p className="text-muted mb-0 font-size-13">
                    {filteredIlanlar.length} ilan listeleniyor
                    {selectedIds.size > 0 && (
                      <span className="text-primary ms-2">({selectedIds.size} seçili)</span>
                    )}
                  </p>
                </div>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  {selectedIds.size > 0 && (
                    <div className="d-flex gap-1">
                      <Button
                        color="success" size="sm"
                        disabled={bulkLoading}
                        onClick={() => handleBulkAction("aktif")}
                      >
                        {bulkLoading ? <Spinner size="sm" /> : "Aktifleştir"}
                      </Button>
                      <Button
                        color="warning" size="sm"
                        disabled={bulkLoading}
                        onClick={() => handleBulkAction("pasif")}
                      >
                        Pasifleştir
                      </Button>
                      <Button
                        color="danger" size="sm"
                        disabled={bulkLoading}
                        onClick={() => handleBulkAction("sil")}
                      >
                        Sil
                      </Button>
                    </div>
                  )}
                  <Button color="light" size="sm" className="border" onClick={fetchAll} disabled={isLoading}>
                    <i className="bx bx-refresh me-1" />Yenile
                  </Button>
                </div>
              </div>

              {/* Filtreler */}
              <Row className="g-2 mb-3">
                <Col md={3}>
                  <Input
                    type="search"
                    bsSize="sm"
                    placeholder="İlan adı, satıcı, ID ara..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Input
                    type="select" bsSize="sm"
                    value={filterDurum}
                    onChange={e => setFilterDurum(e.target.value)}
                  >
                    <option value="all">Tüm Durumlar</option>
                    <option value="aktif">Aktif</option>
                    <option value="pasif">Pasif</option>
                    <option value="beklemede">Beklemede</option>
                  </Input>
                </Col>
                <Col md={3}>
                  <Input
                    type="select" bsSize="sm"
                    value={filterKategori}
                    onChange={e => setFilterKategori(e.target.value)}
                  >
                    <option value="all">Tüm Kategoriler</option>
                    {kategoriler.map(k => (
                      <option key={k.ID} value={k.ID}>{k.AD}</option>
                    ))}
                  </Input>
                </Col>
                <Col md={2}>
                  <Input
                    type="select" bsSize="sm"
                    value={filterTip}
                    onChange={e => setFilterTip(e.target.value)}
                  >
                    <option value="all">Tüm Tipler</option>
                    <option value="satis">Satış</option>
                    <option value="teklif">Teklif Açık</option>
                    <option value="premium">Premium</option>
                  </Input>
                </Col>
                <Col md={2}>
                  <Button
                    color="light" size="sm" className="border w-100"
                    onClick={() => {
                      setFilterDurum("all");
                      setFilterKategori("all");
                      setFilterTip("all");
                      setSearchQuery("");
                    }}
                  >
                    <i className="bx bx-x me-1" />Sıfırla
                  </Button>
                </Col>
              </Row>

              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Yükleniyor...</p>
                </div>
              ) : (
                <TableContainer
                  columns={columns}
                  data={filteredIlanlar}
                  isGlobalFilter={false}
                  isPagination={true}
                  isCustomPageSize={true}
                  tableClass="table-hover table-bordered align-middle nowrap w-100 mb-0"
                  theadClass="table-light"
                  paginationWrapper="d-flex justify-content-end mt-3 pagination-rounded"
                />
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* ══ DETAY MODAL ══════════════════════════════════════════ */}
      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="xl" scrollable>
        <ModalHeader toggle={() => setDetailModal(false)} className="bg-light">
          <span className="fw-bold text-primary">#{detailIlan?.ID}</span>
          {" — "}{detailIlan?.AD}
        </ModalHeader>
        <ModalBody className="p-0">
          {detailLoading ? (
            <div className="text-center py-5"><Spinner color="primary" /></div>
          ) : detailIlan && (
            <>
              <Nav tabs className="nav-tabs-custom px-3 pt-2 bg-white border-bottom">
                {[
                  { id: "1", icon: "bx-info-circle", label: "Genel" },
                  { id: "2", icon: "bx-images", label: `Medya (${ilanMedya.length})` },
                  { id: "3", icon: "bx-message-square-dots", label: `Teklifler (${ilanTeklifler.length})` },
                  { id: "4", icon: "bx-bar-chart-alt-2", label: `Değerlemeler (${ilanDegerlemeler.length})` },
                  { id: "5", icon: "bx-error-circle", label: `Şikayetler (${ilanSikayetler.length})` },
                ].map(tab => (
                  <NavItem key={tab.id}>
                    <NavLink
                      className={classnames({ active: detailTab === tab.id })}
                      onClick={() => setDetailTab(tab.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <i className={`bx ${tab.icon} me-1`} />{tab.label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={detailTab} className="p-3">
                {/* Tab 1: Genel */}
                <TabPane tabId="1">
                  <Row>
                    <Col md={4}>
                      {detailIlan.GORSEL_YOLU ? (
                        <img
                          src={detailIlan.GORSEL_YOLU}
                          alt={detailIlan.AD}
                          className="img-fluid rounded shadow-sm w-100 mb-3"
                          style={{ maxHeight: 260, objectFit: "cover" }}
                        />
                      ) : (
                        <div
                          className="bg-light border rounded d-flex align-items-center justify-content-center text-muted mb-3"
                          style={{ height: 180 }}
                        >
                          <i className="bx bx-image display-4" />
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-1 mb-2">
                        {statusBadge(detailIlan.AKTIF_DURUM)}
                        {detailIlan.IsPremium && <Badge color="warning">Premium</Badge>}
                        {detailIlan.IsBold && <Badge color="dark">Kalın</Badge>}
                        {detailIlan.IsColored && <Badge color="info">Renkli</Badge>}
                        {detailIlan.IsStory && <Badge style={{ background: "#7367f0" }}>Story</Badge>}
                        {detailIlan.SATIS_MI && <Badge color="primary">Satış</Badge>}
                        {detailIlan.TEKLIFE_ACIK_MI && <Badge color="success">Teklif Açık</Badge>}
                        {detailIlan.DEGERLEMEYE_ACIK_MI && <Badge color="secondary">Değerleme</Badge>}
                      </div>
                    </Col>
                    <Col md={8}>
                      <Row className="g-3">
                        {[
                          ["İlan Adı", detailIlan.AD],
                          ["Kategori", detailIlan.KATEGORILER?.AD || "-"],
                          ["Satıcı", detailIlan.KULLANICILAR?.AD_SOYAD || "-"],
                          ["E-posta", detailIlan.KULLANICILAR?.Mail_Adress || "-"],
                          ["Fiyat", detailIlan.FIYAT ? `${Number(detailIlan.FIYAT).toLocaleString("tr-TR")} ₺` : "-"],
                          ["İndirimli Fiyat", detailIlan.INDIRIMLI_FIYAT ? `${Number(detailIlan.INDIRIMLI_FIYAT).toLocaleString("tr-TR")} ₺` : "-"],
                          ["Birim Fiyat", detailIlan.BIRIM_FIYAT ? `${detailIlan.BIRIM_FIYAT} ₺ / ${detailIlan.URUN_BIRIMI || "birim"}` : "-"],
                          ["Stok", detailIlan.STOK_ADEDI ?? "-"],
                          ["Görüntülenme", detailIlan.GÖRÜNTÜLENME_SAYISI ?? 0],
                          ["Konum", [detailIlan.KONUM_IL, detailIlan.KONUM_ILCE].filter(Boolean).join(" / ") || "-"],
                          ["Popülerlik Etiketi", detailIlan.POPULERLIK_ETIKETI || "-"],
                          ["Oluşturma Tarihi", detailIlan.OLUSTURMA_TARIHI ? new Date(detailIlan.OLUSTURMA_TARIHI).toLocaleString("tr-TR") : "-"],
                          ["Güncelleme Tarihi", detailIlan.GUNCELLEME_TARIHI ? new Date(detailIlan.GUNCELLEME_TARIHI).toLocaleString("tr-TR") : "-"],
                          ["Bitiş Tarihi", detailIlan.BITIS_TARIHI ? new Date(detailIlan.BITIS_TARIHI).toLocaleString("tr-TR") : "-"],
                        ].map(([label, value]) => (
                          <Col sm={6} key={label}>
                            <Label className="text-muted mb-0 font-size-11">{label}</Label>
                            <p className="fw-medium mb-0">{value}</p>
                          </Col>
                        ))}
                        {detailIlan.ACIKLAMA && (
                          <Col sm={12}>
                            <Label className="text-muted mb-1 font-size-11">Açıklama</Label>
                            <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{detailIlan.ACIKLAMA}</p>
                          </Col>
                        )}
                        {detailIlan.TESLIMAT && (
                          <Col sm={12}>
                            <Label className="text-muted mb-1 font-size-11">Teslimat Bilgisi</Label>
                            <p className="mb-0">{detailIlan.TESLIMAT}</p>
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </TabPane>

                {/* Tab 2: Medya */}
                <TabPane tabId="2">
                  {ilanMedya.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-images display-4 mb-2 opacity-50" />
                      <p>Medya bulunamadı.</p>
                    </div>
                  ) : (
                    <Row className="g-3">
                      {ilanMedya.map((m, idx) => (
                        <Col key={idx} xs={6} md={4} lg={3}>
                          <Card className="border shadow-none h-100">
                            <div style={{ background: "#f8f9fa", height: 160, overflow: "hidden" }}>
                              {m.MEDYA_TIP === "video" ? (
                                <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                  <i className="bx bx-play-circle display-4" />
                                </div>
                              ) : (
                                <img
                                  src={m.MEDYA_URL || m.MEDYA_YOLU}
                                  alt={m.BASLIK || `Medya ${idx + 1}`}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              )}
                            </div>
                            <CardBody className="p-2">
                              <p className="mb-0 font-size-11 text-muted text-truncate">
                                {m.BASLIK || `Sıra: ${m.SIRA ?? idx + 1}`}
                              </p>
                              <Badge color="light" className="text-dark font-size-10 border">{m.MEDYA_TIP}</Badge>
                            </CardBody>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </TabPane>

                {/* Tab 3: Teklifler */}
                <TabPane tabId="3">
                  {ilanTeklifler.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-message-square-dots display-4 mb-2 opacity-50" />
                      <p>Henüz teklif yok.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-hover align-middle mb-0" bordered>
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Kullanıcı</th>
                            <th>Teklif Tutarı</th>
                            <th>Tarih</th>
                            <th>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ilanTeklifler.map(t => (
                            <tr key={t.ID}>
                              <td className="text-muted">#{t.ID}</td>
                              <td>
                                <div className="fw-medium">{t.KULLANICILAR?.AD_SOYAD || "-"}</div>
                                <small className="text-muted">{t.KULLANICILAR?.Mail_Adress}</small>
                              </td>
                              <td className="fw-bold text-primary">
                                {Number(t.TEKLIF_TUTARI).toLocaleString("tr-TR")} ₺
                              </td>
                              <td>
                                {t.TEKLIF_TARIHI
                                  ? new Date(t.TEKLIF_TARIHI).toLocaleString("tr-TR")
                                  : "-"}
                              </td>
                              <td>
                                <Badge color={teklifDurumColor(t.DURUM)}>
                                  {teklifDurumText(t.DURUM)}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </TabPane>

                {/* Tab 4: Değerlemeler */}
                <TabPane tabId="4">
                  {ilanDegerlemeler.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-bar-chart-alt-2 display-4 mb-2 opacity-50" />
                      <p>Değerleme bulunamadı.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-hover align-middle mb-0" bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Kullanıcı</th>
                            <th>Önerilen Fiyat</th>
                            <th>Tarih</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ilanDegerlemeler.map(d => (
                            <tr key={d.ID}>
                              <td>{d.KULLANICILAR?.AD_SOYAD || "-"}</td>
                              <td className="fw-bold text-success">
                                {Number(d.ONERILEN_FIYAT).toLocaleString("tr-TR")} ₺
                              </td>
                              <td>{d.TARIH ? new Date(d.TARIH).toLocaleString("tr-TR") : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </TabPane>

                {/* Tab 5: Şikayetler */}
                <TabPane tabId="5">
                  {ilanSikayetler.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-shield-alt-2 display-4 mb-2 opacity-50" />
                      <p>Şikayet bulunamadı.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-hover align-middle mb-0" bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Kullanıcı</th>
                            <th>Şikayet Tipi</th>
                            <th>Açıklama</th>
                            <th>Tarih</th>
                            <th>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ilanSikayetler.map(s => (
                            <tr key={s.ID}>
                              <td>{s.KULLANICILAR?.AD_SOYAD || "-"}</td>
                              <td><Badge color="warning">{s.SIKAYET_TIP}</Badge></td>
                              <td className="font-size-12" style={{ maxWidth: 200 }}>
                                <span className="text-truncate d-block">{s.ACIKLAMA || "-"}</span>
                              </td>
                              <td>{s.TARIH ? new Date(s.TARIH).toLocaleDateString("tr-TR") : "-"}</td>
                              <td>
                                <Badge color={s.DURUM === "çözüldü" ? "success" : "danger"}>
                                  {s.DURUM}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </TabPane>
              </TabContent>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary" outline
            onClick={() => {
              setDetailModal(false);
              setEditIlan({ ...detailIlan });
              setEditModal(true);
            }}
          >
            <i className="bx bx-pencil me-1" />Düzenle
          </Button>
          <Button color="secondary" outline onClick={() => setDetailModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>

      {/* ══ HIZLI DÜZENLEME MODAL ════════════════════════════════ */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)} centered size="md">
        <ModalHeader toggle={() => setEditModal(false)} className="bg-light">
          İlan Düzenle — <span className="text-primary">#{editIlan?.ID}</span>
        </ModalHeader>
        <ModalBody>
          {editIlan && (
            <Row className="g-3">
              <Col sm={12}>
                <Label className="fw-semibold">{editIlan.AD}</Label>
              </Col>
              <Col sm={6}>
                <FormGroup className="mb-0">
                  <Label>Fiyat (₺)</Label>
                  <Input
                    type="number"
                    value={editIlan.FIYAT || ""}
                    onChange={e => setEditIlan({ ...editIlan, FIYAT: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={6}>
                <FormGroup className="mb-0">
                  <Label>İndirimli Fiyat (₺)</Label>
                  <Input
                    type="number"
                    value={editIlan.INDIRIMLI_FIYAT || ""}
                    onChange={e => setEditIlan({ ...editIlan, INDIRIMLI_FIYAT: e.target.value })}
                    placeholder="Boş = indirim yok"
                  />
                </FormGroup>
              </Col>
              <Col sm={6}>
                <FormGroup className="mb-0">
                  <Label>Stok Adedi</Label>
                  <Input
                    type="number"
                    value={editIlan.STOK_ADEDI || ""}
                    onChange={e => setEditIlan({ ...editIlan, STOK_ADEDI: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={6}>
                <FormGroup className="mb-0">
                  <Label>Durum</Label>
                  <Input
                    type="select"
                    value={editIlan.AKTIF_DURUM || ""}
                    onChange={e => setEditIlan({ ...editIlan, AKTIF_DURUM: e.target.value })}
                  >
                    <option value="aktif">Aktif</option>
                    <option value="pasif">Pasif</option>
                    <option value="beklemede">Beklemede</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col sm={12}>
                <FormGroup className="mb-0">
                  <Label>Bitiş Tarihi</Label>
                  <Input
                    type="datetime-local"
                    value={editIlan.BITIS_TARIHI
                      ? new Date(editIlan.BITIS_TARIHI).toISOString().slice(0, 16)
                      : ""}
                    onChange={e => setEditIlan({ ...editIlan, BITIS_TARIHI: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={12}>
                <FormGroup className="mb-0">
                  <Label>Açıklama</Label>
                  <Input
                    type="textarea"
                    rows={3}
                    value={editIlan.ACIKLAMA || ""}
                    onChange={e => setEditIlan({ ...editIlan, ACIKLAMA: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col sm={12}>
                <div className="bg-light p-3 rounded border">
                  <Label className="fw-semibold mb-2 d-block">Etiketler</Label>
                  <Row>
                    {[
                      { key: "IsPremium", label: "Premium", color: "warning" },
                      { key: "IsBold", label: "Kalın Yazı", color: "dark" },
                      { key: "TEKLIFE_ACIK_MI", label: "Teklif Açık", color: "success" },
                    ].map(sw => (
                      <Col xs={6} sm={4} key={sw.key}>
                        <FormGroup switch className="mb-2">
                          <Input
                            type="switch"
                            checked={!!editIlan[sw.key]}
                            onChange={e => setEditIlan({ ...editIlan, [sw.key]: e.target.checked })}
                          />
                          <Label check className={sw.color === "warning" ? "text-warning fw-bold" : ""}>
                            {sw.label}
                          </Label>
                        </FormGroup>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setEditModal(false)}>İptal</Button>
          <Button color="primary" onClick={handleEditSave} disabled={editLoading}>
            {editLoading ? <Spinner size="sm" /> : <i className="bx bx-check me-1" />}
            Kaydet
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default IlanYonetimi;
